import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Asset } from 'contentful';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import { useFeatureFlag } from '@order/LaunchDarkly';

// ─────────────────────────────────────────────────────────────────────────────

export const useGroupOrderingContent = (params: Params = {}) => {
  const { pause } = params;

  const { formatMessage } = useIntl();

  const isGroupOrderingEnabled = useFeatureFlag('CELS-5355-order-group-ordering-enabled');

  // ─── Remote Data ─────────────────────────────────────────────────────

  const { data, fetching } = useContentfulContentTypeEntry<Content>({
    contentType: GROUP_ORDERING_CONTENT_ENTRY_ID,
    pause: !isGroupOrderingEnabled ? true : pause,
  });

  const fields = data?.fields;

  // ─── Derived Data ────────────────────────────────────────────────────

  const mappedContent = useMemo<MappedContent>(() => {
    return {
      entryPointCtaLabel: fields?.entryPointCtaLabel ?? formatMessage(messages.entryPointCtaLabel),
      introModalNextButtonLabel:
        fields?.introModalNextButtonLabel ?? formatMessage(messages.nextButtonLabel),
      introModalCancelButtonLabel:
        fields?.introModalCancelButtonLabel ?? formatMessage(messages.cancelButtonLabel),
      introModalStartOrderButtonLabel:
        fields?.introModalStartOrderButtonLabel ?? formatMessage(messages.startOrderButtonLabel),
      introCards: fields?.introModalSlides ?? [],
    };
  }, [fields, formatMessage]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    isFetching: fetching,
    mappedContent,
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  entryPointCtaLabel: {
    defaultMessage: 'Start group order',
    description: 'Home | Group ordering | Fallback entry point CTA label',
  },
  nextButtonLabel: {
    defaultMessage: 'Next',
    description: 'Home | Group ordering intro | Fallback next button label',
  },
  cancelButtonLabel: {
    defaultMessage: 'Cancel',
    description: 'Home | Group ordering intro | Fallback cancel button label',
  },
  startOrderButtonLabel: {
    defaultMessage: 'Start group order',
    description: 'Home | Group ordering intro | Fallback start order button label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const GROUP_ORDERING_CONTENT_ENTRY_ID = 'orderGroupOrdering';

// ─── Types ───────────────────────────────────────────────────────────────────

export type IntroCard = {
  fields: {
    badge?: string;
    title: string;
    text: string;
    image?: Asset;
  };
};

type Content = {
  entryPointCtaLabel: string;
  introModalNextButtonLabel: string;
  introModalCancelButtonLabel: string;
  introModalStartOrderButtonLabel: string;
  introModalSlides?: IntroCard[];
};

type MappedContent = {
  entryPointCtaLabel: string;
  introModalNextButtonLabel: string;
  introModalCancelButtonLabel: string;
  introModalStartOrderButtonLabel: string;
  introCards: IntroCard[];
};

type Params = {
  pause?: boolean;
};
